import type {StaticImageData} from 'next/image';
import regex from '@/regex';

// Returns the CDN base URL from environment variables
function normalizeBase(): string {
    const raw = typeof process === 'undefined' ? undefined : process.env.NEXT_PUBLIC_IMAGE_CDN_BASE?.trim();
    if (!raw) {
        throw new Error('NEXT_PUBLIC_IMAGE_CDN_BASE is required — set it in .env');
    }
    // Scan from the right: avoids regex backtracking on a /+$ pattern (O(n) guaranteed).
    let end = raw.length;
    while (end > 0 && raw[end - 1] === '/') end--;
    return end < raw.length ? raw.slice(0, end) : raw;
}

/** Returns a full CDN URL using the base environment variable */
export function cdnAsset(path: string): string {
    const p = path.trim().replace(regex.LEADING_SLASHES, '');
    return `${normalizeBase()}/${p}`;
}

/** Returns a URL specifically inside the /images folder */
export function cdnUrl(path: string): string {
    let p = path.trim().replace(regex.LEADING_SLASHES, '');
    if (p.startsWith('images/')) {
        p = p.slice('images/'.length);
    }
    return cdnAsset(`images/${p}`);
}

// 8×8 gray webp — inline blur placeholder, no extra network request
const BLUR_PLACEHOLDER =
    'data:image/webp;base64,UklGRlYAAABXRUJQVlA4IEoAAADQAQCdASoIAAgAAkA4JZQCdAEO/gHOAAD++P7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v4A';

/**
 * Simulates a static image import for CDN assets.
 * Placeholder width/height satisfy next/image; CSS controls actual sizing.
 */
export function cdnImage(path: string): StaticImageData {
    return {
        src: cdnUrl(path),
        width: 1200,
        height: 800,
        blurDataURL: BLUR_PLACEHOLDER
    };
}
